'use strict';
/* ==========================================================================
   levels/video.js — step 1, THE POWER GRAB: the interactive video (shared/engine/v2/core/player.js), collage style,
   one scene per script segment (content/script.json s1–s5; each segment's "visual" is the storyboard).

   Every scene is a PURE FUNCTION of the part time t: beats are keyed to the dialogue through ctx.L / ctx.E / ctx.W
   (via cues(), which turns the ±Infinity of other parts into ±1e6), so the choreography follows whatever the voice
   timing is (text timing now, the recorded clips later, with no code change) and the player can seek, replay and run
   at 25×. Branches: a reply cue is min() over the question's reply ids (branch()): in the reply part only the branch
   that plays is finite, in the lines part all are +∞ (not yet), in the after part all are −∞ (done). A visual that
   belongs to ONE branch is gated on the answer (ansOf()).

   Honesty (DESIGN.md §4): every number on screen comes from core-local/civics.js (Civics.CHAMBERS, twoThirdsNeeded,
   majorityNeeded, overrides, VETOES, the Marbury year from Civics.RULES['judicial-review'].cite) or from the
   question's `given` / `working` evaluated by the shared bank evaluator (Bank.fill / Bank.eval; "dec2" = two
   decimals, "int"). The two exceptions are storyboard props, not answers: the nine empty chairs (JUSTICES, facts 6.3:
   not in civics.js) and the book prop "The Spirit of the Laws, 1748" (facts 2.4). The Marbury quote is facts row 2.3.

   Staging (like lessons/ground): each segment opens on a chapter card that tears away; Rex (left) and the Judge
   (right) stand full size ("two": captions in the top band on the speaker's side) or shrink to bottom-corner insets
   ("inset": captions in the bottom band beside them) when the boards need the screen. Portrait: the player puts the
   captions in the dock; the working, the veto record and the Marbury quote are also written into the dock (Dock.text).
   Cost: backdrops are baked once into layers (OFF budget, 0.75 zoom); paper pieces are cached sprites; the House's 435
   tiles are two baked layers and a clip; no per-frame shadowOn().
   Art: IMG.rex / rex_point / judge / judge_point (via core-local/hooks.js poses), capitol / whitehouse / court /
   gavel / quill (via Civic.building / Civic.tool), podium, scroll — each drawn only if loaded, else a paper stand-in.
   Debug / QA: window.BranchesVideo (nums(), working(), lastSteps, BODY, STEPCUE).
   ========================================================================== */
(function () {
  const S = Player.scenes;
  const HANDF = "'Caveat Brush'", TYPE = "'Special Elite'", SLAB = "'Alfa Slab One'", SERIF = "'Abril Fatface'";
  const INK = '#2b2420', PENCIL = 'rgba(43,36,32,0.62)', RED = '#b3261e', GOLD_INK = '#7d5f14';
  const KC = () => Civic.C;
  const BRS = ['legislative', 'executive', 'judicial'];
  const TITLE_PAL = [['#1f2f55', '#f3ead7'], ['#9e2b25', '#f3ead7'], ['#f3ead7', '#1f2f55'], ['#fffdf7', '#9e2b25'], ['news', '#2b2420'], ['#b08a2e', '#2b2420']];
  // storyboard props that are not in civics.js (see the header)
  const JUSTICES = 9;                                                  // local on purpose (not in civics.js): facts-branches.md 6.3 (28 U.S.C. §1)
  const BOOK = { lines: ['The Spirit', 'of the Laws'], year: '1748' }; // local prop: facts-branches.md 2.4 (Montesquieu, 1748; DESIGN.md §2)
  const MARBURY = {                                                    // facts-branches.md 2.3 (the opinion's exact words, corrected), 6.1
    title: 'MARBURY v. MADISON', who: 'Chief Justice John Marshall:',
    quote: 'It is emphatically the province and duty of the Judicial Department to say what the law is.',   // 5 U.S. 137, 177
    small: 'first law of Congress struck down by the Court',
  };
  const BILL = ['NO HOMEWORK', 'FRIDAYS ACT'];

  /* ---------------- small helpers ---------------- */
  const fin = x => (x === Infinity ? 1e6 : x === -Infinity ? -1e6 : x);
  const cues = ctx => ({ L: id => fin(ctx.L(id)), E: id => fin(ctx.E(id)), W: (id, w, n = 1) => fin(ctx.W(id, w, n)) });
  const win = (t, a, b, ramp = 0.35) => Math.min(E.inOutSine(P(t, a, a + ramp)), 1 - E.inOutSine(P(t, b, b + ramp)));
  const fmtInt = n => Math.round(n).toLocaleString('en-US');
  const segs = () => Game.content.script.segments;
  const segIndex = seg => segs().indexOf(seg);
  const segById = id => segs().find(s => s.id === id);
  const fx = (name, alt) => (Audio.has(name) ? name : alt);
  const ansOf = (ctx, part) => { const q = part.seg.question; if (!q) return null; return ctx.answer != null ? ctx.answer : part.answer != null ? part.answer : q.correct; };
  function fontOk() { return typeof document === 'undefined' || !document.fonts || document.fonts.status === 'loaded'; }
  const widths = new Map();
  function tw(str, font) {
    const k = font + '|' + str; let w = widths.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) { if (widths.size > 2000) widths.clear(); widths.set(k, w); } }
    return w;
  }
  function wrapLines(str, maxW, size, font = HANDF) {
    const words = String(str).split(/\s+/), out = []; let cur = '';
    for (const w of words) { const nx = cur ? cur + ' ' + w : w; if (cur && tw(nx, `${size}px ${font}`) > maxW) { out.push(cur); cur = w; } else cur = nx; }
    if (cur) out.push(cur);
    return out;
  }
  function alpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  /* static text (no write-on) */
  function txt(str, x, y, size, o = {}) {
    g.save(); g.font = `${size}px ${o.font || HANDF}`; g.fillStyle = o.color || INK; g.textAlign = o.align || 'center'; g.textBaseline = 'middle';
    if (o.alpha != null) g.globalAlpha *= o.alpha;
    if (o.rot) { g.translate(x, y); g.rotate(o.rot); g.fillText(str, 0, 0); } else g.fillText(str, x, y);
    g.restore();
  }
  /* the min over a question's reply ids of a cue word per branch (tbl: {replyId: word | [word, n]}) */
  function branch(c, tbl) {
    let best = Infinity;
    for (const id in tbl) { const v = tbl[id]; if (v == null) continue; const [w, n] = Array.isArray(v) ? v : [v, 1]; best = Math.min(best, c.W(id, w, n)); }
    return best;
  }
  const replyIds = q => q.right.concat(...Object.values(q.wrong || {}));
  const replyStart = (c, q) => Math.min(...replyIds(q).map(id => c.L(id)));
  /* keyframed position: keys [[t, x, y, s, dur?]] → {x, y, s}; each move eases over its dur from its key time */
  function track(t, keys, dur = 0.6) {
    let x = keys[0][1], y = keys[0][2], s = keys[0][3] ?? 1;
    for (let i = 1; i < keys.length; i++) {
      const k = keys[i]; if (t <= k[0]) continue;
      const e = E.inOutCubic(P(t, k[0], k[0] + (k[4] ?? dur)));
      x = lerp(x, k[1], e); y = lerp(y, k[2], e); s = lerp(s, k[3] ?? s, e);
    }
    return { x, y, s };
  }

  /* ======================================================================
     Layers: static art baked once into an off-screen canvas (the engine's OFF budget evicts them like sprites).
     layer(key, x, y, w, h, draw, {zoom}) — draw() paints the rect (x, y, w, h) in the caller's coordinates.
     ====================================================================== */
  const LY = new Map();
  function layer(key, x, y, w, h, draw, o = {}) {
    const res = +Math.min(3, Math.max(0.3, DS * (o.zoom || 1))).toFixed(3);
    let e = LY.get(key);
    if (!e || e.dead || e.res !== res) {
      if (e && !e.dead) OFF.drop(e);
      LY.delete(key);
      const pw = Math.ceil(w * res), ph = Math.ceil(h * res);
      if (!FX.sprites || typeof _bake !== 'function' || !fontOk() || !OFF.room(pw * ph * 4)) { draw(); return false; }
      const cv = _bake(pw, ph, cx => cx.setTransform(res, 0, 0, res, -x * res, -y * res), draw);
      e = OFF.add(cv, { res, x, y, w, h });
      const me = e; e.drop = () => { if (LY.get(key) === me) LY.delete(key); };
      LY.set(key, e);
    }
    e.frame = OFF.frame;
    g.drawImage(e.c, x, y, e.c.width / res, e.c.height / res);
    return true;
  }

  /* ---------------- backdrops (baked) ---------------- */
  function bgGym() {
    layer('bg_gym', 0, 0, 1920, 1080, () => {
      const C = KC();
      g.fillStyle = paperTile(C.parchment, 4); g.fillRect(0, 0, 1920, 1080);
      paperRect(960, 800, 2040, 250, '#dcc8a0', { seed: 301, torn: true, depth: 0.8, boil: false });
      for (let i = 0; i < 16; i++) ink([[i * 128 + 20, 700], [i * 128 + 24, 910]], { w: 2, color: 'rgba(120,90,50,0.25)', seed: 310 + i, passes: 1, step: 60 });
      [[64, -0.01], [1856, 0.012]].forEach(([x, r], i) => {
        paperRect(x, 470, 210, 1000, C.navy, { seed: 302 + i, torn: true, depth: 1.3, boil: false, rot: r });
        for (let k = -1; k <= 1; k++) ink([[x + k * 52, 0], [x + k * 50 + 6, 960]], { w: 3, color: 'rgba(10,18,40,0.45)', seed: 320 + i * 3 + k, passes: 1, step: 80 });
      });
      paperRect(960, 1010, 2060, 210, C.kraft, { seed: 305, torn: true, depth: 1.2, boil: false });
      for (let i = 0; i < 9; i++) ink([[0, 930 + i * 18 + (i % 2) * 4], [1920, 932 + i * 18]], { w: 1.6, color: 'rgba(90,60,30,0.25)', seed: 330 + i, passes: 1, step: 120 });
      // the banner, hung along the front of the stage
      paperRect(960, 985, 900, 76, C.navy, { seed: 306, depth: 1.2, boil: false, rot: -0.004 });
      paperRect(960, 1016, 900, 10, C.brick, { seed: 307, depth: 0, boil: false });
      g.save(); g.translate(960, 984); g.rotate(-0.004); g.font = `32px ${SLAB}`; g.fillStyle = C.cream; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillText('CORAL RIDGE MIDDLE · STUDENT COUNCIL', 0, 2); g.restore();
      tape(520, 950, 100, -0.4, undefined, 308); tape(1400, 950, 100, 0.4, undefined, 309);
    }, { zoom: 0.75 });
  }
  function bgBoard() {
    layer('bg_board', 0, 0, 1920, 1080, () => {
      g.fillStyle = paperTile('#cda673', 23); g.fillRect(0, 0, 1920, 1080);
      g.fillStyle = 'rgba(80,50,20,0.14)'; g.fillRect(0, 0, 1920, 26); g.fillRect(0, 1054, 1920, 26); g.fillRect(0, 0, 26, 1080); g.fillRect(1894, 0, 26, 1080);
      const r = rng(41);
      for (let i = 0; i < 70; i++) { g.fillStyle = `rgba(70,40,15,${0.05 + r() * 0.08})`; g.beginPath(); g.arc(r() * 1920, r() * 1080, 1 + r() * 3, 0, TAU); g.fill(); }
    }, { zoom: 0.75 });
  }
  const WALL = '#e8dab9';
  function bgCourt() {
    layer('bg_court', 0, 0, 1920, 1080, () => {
      const C = KC();
      g.fillStyle = paperTile(WALL, 6); g.fillRect(0, 0, 1920, 1080);
      for (let i = 0; i < 7; i++) paperRect(160 + i * 267, 420, 70, 900, '#efe4c9', { seed: 340 + i, depth: 0.5, boil: false });
      paperRect(960, 60, 2000, 60, C.gold, { seed: 350, depth: 0.8, boil: false, torn: true });
      paperRect(960, 960, 2060, 260, '#b98a58', { seed: 351, torn: true, depth: 1.2, boil: false });
    }, { zoom: 0.75 });
  }
  function bgParch() {
    layer('bg_parch', 0, 0, 1920, 1080, () => {
      g.fillStyle = paperTile(KC().parchment, 7); g.fillRect(0, 0, 1920, 1080);
      texScrap('tex_news', 160, 150, 360, 260, { rot: -0.08, seed: 360, depth: 0.6, tint: KC().parchment, tintAlpha: 0.6 });
      texScrap('tex_news', 1780, 980, 380, 240, { rot: 0.06, seed: 361, depth: 0.6, tint: KC().parchment, tintAlpha: 0.6 });
    }, { zoom: 0.75 });
  }

  /* ---------------- paper bits ---------------- */
  function strike(x0, y0, x1, y1, prog, seed = 1, color = RED, w = 9) { ink([[x0, y0], [(x0 + x1) / 2 + 4, (y0 + y1) / 2 - 3], [x1, y1]], { w, color, seed, prog, passes: 2, step: 10 }); }
  function check(x, y, s, prog, seed = 1, color = KC().navy) { ink([[x - 34 * s, y - 2 * s], [x - 8 * s, y + 26 * s], [x + 40 * s, y - 38 * s]], { w: 11 * s, color, seed, prog, passes: 2, step: 8 }); }
  /* a paper label: str on a paper rect (x, y = centre) */
  function tag(str, x, y, size, s = 1, o = {}) {
    if (s <= 0.001) return;
    g.save(); g.globalAlpha *= o.alpha ?? 1; g.translate(x, y); g.rotate(o.rot ?? -0.04); g.scale(s, s);
    const font = `${size}px ${o.font || HANDF}`, w = Math.round(tw(str, font) + size * 0.9), h = Math.round(size * 1.4);
    paperRect(0, 0, w, h, o.color || KC().cream, { seed: o.seed || 41, depth: o.depth ?? 1.0, outline: true });
    g.font = font; g.fillStyle = o.ink || INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(str, 0, size * 0.05);
    g.restore();
  }
  /* a sticky note with hand-written lines; strike = a red line across it at that time */
  function sticky(lines, x, y, t0, o = {}) {
    const s = pop(T, t0, 0.35); if (s <= 0) return;
    const w = o.w || 280, size = o.size || 34, seed = o.seed || 610, h = Math.round(44 + lines.length * size * 1.15);
    g.save(); g.globalAlpha *= o.alpha ?? 1; g.translate(x, y); g.rotate(o.rot ?? -0.05); g.scale(s, s);
    paperRect(0, 0, w, h, o.color || '#f2d479', { seed, depth: 1.2 });
    lines.forEach((l, i) => hand(l, 0, -h / 2 + 22 + size * 0.62 + i * size * 1.15, size, t0 + 0.1 + i * 0.12, { color: o.ink || INK, seed: seed + i + 1, dur: 0.3 }));
    if (o.strike != null) strike(-w / 2 + 16, h / 2 - 18, w / 2 - 16, -h / 2 + 18, P(T, o.strike, o.strike + 0.35), seed + 9);
    tape(0, -h / 2 + 2, 96, 0.05, undefined, seed + 20);
    g.restore();
  }
  /* a rubber stamp (no knock-out specks: they would punch through the whole frame) */
  function stampMark(text, x, y, s, o = {}) {
    if (s <= 0.001) return;
    const size = o.size || 72, color = o.color || RED, font = `${size}px ${TYPE}`;
    const w = Math.round(tw(text, font) + size * 0.9), h = Math.round(size * 1.5);
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.12); g.scale(s, s); g.globalAlpha *= o.alpha ?? 0.9;
    g.strokeStyle = color; g.fillStyle = color; g.lineJoin = 'round';
    g.lineWidth = size * 0.1; pathPts(rectPts(w, h, 40 + text.length, true, 4)); g.stroke();
    g.lineWidth = size * 0.035; pathPts(rectPts(w - Math.round(size * 0.26), h - Math.round(size * 0.26), 41 + text.length, true, 3)); g.stroke();
    g.font = font; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(text, 0, size * 0.07);
    g.restore();
  }
  const stampS = (t, t0) => (t < t0 ? 0 : E.outBack(P(t, t0, t0 + 0.25), 1.6));
  /* a parchment scroll unrolled to u (0..1) from its top; content() draws inside it (clipped) */
  function scroll(x, y, w, h, u, content, seed = 11) {
    if (u <= 0.001) return;
    const top = y - h / 2, vis = h * u;
    g.save(); g.beginPath(); g.rect(x - w / 2 - 40, top - 30, w + 80, vis + 30); g.clip();
    Civic.parchment(x, y, w, h, { seed, depth: 1.3 });
    if (content) content();
    g.restore();
    paperRect(x, top, w + 44, 30, '#d8c08e', { seed: seed + 1, depth: 0.9, outline: true, boil: false });
    paperRect(x, top + vis, w + 44, 30, '#d8c08e', { seed: seed + 2, depth: 0.9, outline: true, boil: false });
  }
  /* a dashed ink arc from a to b, drawn on to prog (quadratic with a bulge, like inkArrow) */
  function dashArrow(x1, y1, x2, y2, { bulge = 0, w = 5, color = INK, prog = 1, seed = 1 } = {}) {
    if (prog <= 0) return;
    const mx = (x1 + x2) / 2, my = (y1 + y2) / 2, dx = x2 - x1, dy = y2 - y1, L = Math.hypot(dx, dy);
    const cx = mx - dy / L * bulge, cy = my + dx / L * bulge, n = 24, m = Math.max(1, Math.round(n * prog));
    const q = u => [(1 - u) * (1 - u) * x1 + 2 * (1 - u) * u * cx + u * u * x2, (1 - u) * (1 - u) * y1 + 2 * (1 - u) * u * cy + u * u * y2];
    g.save(); g.setLineDash([20, 14]); g.lineWidth = w; g.strokeStyle = color; g.lineCap = 'round';
    g.beginPath(); for (let i = 0; i <= m; i++) { const [px, py] = q(i / n); if (i) g.lineTo(px, py); else g.moveTo(px, py); } g.stroke(); g.restore();
    if (prog >= 0.98) { const a = Math.atan2(y2 - cy, x2 - cx), k = 24; ink([[x2 - Math.cos(a - 0.45) * k, y2 - Math.sin(a - 0.45) * k], [x2, y2], [x2 - Math.cos(a + 0.45) * k, y2 - Math.sin(a + 0.45) * k]], { w, color, seed, step: 8 }); }
  }

  /* ======================================================================
     Staging: puppets, captions, chapter cards (after lessons/ground/levels/video.js)
     ====================================================================== */
  const POS = {
    two: { rex: { x: 250, y: 1088, h: 540, dip: 0 }, judge: { x: 1665, y: 1088, h: 610, dip: 0 } },
    inset: { rex: { x: 124, y: 1098, h: 300, dip: 56 }, judge: { x: 1796, y: 1098, h: 330, dip: 56 } },
  };
  /* keys: [[t, mode] …] → the puppets' {x, y, h} (moves ease over 0.7 s); an inset puppet dips while silent */
  function stage(t, part, keys) {
    const out = {};
    ['rex', 'judge'].forEach(who => {
      const v = Object.assign({}, POS[keys[0][1]][who]);
      for (let i = 1; i < keys.length; i++) {
        const k = keys[i]; if (t <= k[0]) continue;
        const e = E.inOutCubic(P(t, k[0], k[0] + 0.7)), p = POS[k[1]][who];
        ['x', 'y', 'h', 'dip'].forEach(f => { v[f] = lerp(v[f], p[f], e); });
      }
      const talk = part.lines.filter(l => l.who === who).reduce((a, l) => Math.max(a, win(t, l.start - 0.35, l.end + 0.15, 0.3)), 0);
      v.y += v.dip * (1 - talk);
      out[who] = v;
    });
    return out;
  }
  const REX_POINT = new Set(['s1_01', 's1_03', 's1_09', 's2_01', 's2_04', 's3_01', 's4_01', 's4_03', 's5_06', 's5_08']);
  const JUDGE_POINT = new Set(['s1_04', 's1_08', 's1_11', 's2_02', 's2_05', 's3_02', 's3_04', 's3_06', 's4_02', 's4_04', 's4_08', 's5_02', 's5_03', 's5_05']);
  /* both puppets + the caption bubble; o.front() draws in front of Rex (the podium) */
  function cast(t, ctx, st, o = {}) {
    const cap = ctx.caption, id = cap ? cap.id : '', who = cap ? cap.who : '';
    const rp = who === 'rex' && REX_POINT.has(id) ? 'point' : null;
    const jp = who === 'judge' && (JUDGE_POINT.has(id) || ctx.kind === 'reply') ? 'point' : null;
    Puppet.draw('rex', st.rex.x, st.rex.y, st.rex.h, { env: ctx.speaking('rex'), facing: 1, t, pose: rp });
    if (o.front) o.front();
    Puppet.draw('judge', st.judge.x, st.judge.y, st.judge.h, { env: ctx.speaking('judge'), facing: -1, t, pose: jp });
    bubble(ctx, st);
  }
  /* caption bubbles: ≤ 2 rows (wider first, then paged at sentence ends, timed by the words) */
  const BS = () => (Player.captionSize ? Player.captionSize() : 40);
  const pageCache = new Map();
  function pages(l, w) {
    const size = BS(), key = l.id + '|' + w + '|' + l.tokens.length + '|' + size + '|' + l.dur.toFixed(2);
    if (pageCache.has(key)) return pageCache.get(key);
    const toks = l.tokens, n = toks.length, maxW = w - 56;
    g.save(); g.font = `${size}px ${HANDF}`;
    const sp = g.measureText(' ').width, ws = toks.map(k => g.measureText(k.w).width);
    g.restore();
    const rowsOf = (a, b) => { let r = 1, rw = 0; for (let i = a; i < b; i++) { if (rw > 0 && rw + sp + ws[i] > maxW) { r++; rw = 0; } rw += ws[i] + sp; } return r; };
    const out = []; let a = 0;
    while (a < n) {
      let b = a + 1; while (b < n && rowsOf(a, b + 1) <= 2) b++;
      if (b < n) {
        const lo = a + Math.max(1, Math.floor((b - a) * 0.4));
        let cut_ = -1;
        for (const re of [/[.!?]["')]?$/, /[,;:]$/]) { for (let j = b; j >= lo; j--) if (re.test(toks[j - 1].w)) { cut_ = j; break; } if (cut_ > 0) break; }
        if (cut_ > 0) b = cut_;
      }
      out.push([a, b]); a = b;
    }
    const res = out.map(([a_, b_], k) => {
      const t0 = k === 0 ? 0 : Math.max(0, toks[a_].s - 0.15);
      return { t0, text: toks.slice(a_, b_).map(x => x.w).join(' '), rows: rowsOf(a_, b_), tokens: toks.slice(a_, b_).map(x => ({ w: x.w, s: x.s - t0, e: x.e - t0 })) };
    });
    if (pageCache.size > 300) pageCache.clear();
    if (fontOk()) pageCache.set(key, res);
    return res;
  }
  function fit(l, w0, maxW) {
    const BLH = BS() * 1.18;
    for (let w = w0; w <= maxW; w += 40) { const pg = pages(l, w); if (pg.length === 1) return { w, pages: pg, h: pg[0].rows * BLH + 40 }; }
    const pg = pages(l, w0);
    return { w: w0, pages: pg, h: Math.max(...pg.map(x => x.rows)) * BLH + 40 };
  }
  const pageAt = (pg, lt) => { let k = 0; for (let i = 1; i < pg.length; i++) if (lt >= pg[i].t0) k = i; return pg[k]; };
  /* full size: the top band on the speaker's side (the stage below stays clear); inset: the bottom band beside them */
  function bubble(ctx, st) {
    const l = ctx.caption; if (!l) return;
    const p = st[l.who]; if (!p) return;
    const rex = l.who === 'rex', dir = rex ? 1 : -1, side = rex ? 'left' : 'right', k = BS() / 40;
    const tall = p.h > 420;
    const f = fit(l, Math.round(700 * k), Math.round(860 * k));
    const w = f.w, bh = f.h;
    let cx, cy;
    if (tall) { cx = rex ? p.x - 60 + w / 2 : p.x + 60 - w / 2; cy = 84 + bh / 2; }
    else { cx = p.x + dir * (0.2 * p.h + w / 2 + 16); cy = Math.min(876, 968 - bh / 2); }
    cx = Math.max(w / 2 + 16, Math.min(1904 - w / 2, cx));
    cy = Math.max(bh / 2 + 16, cy);
    const cap = { x: cx, y: cy, side, w };
    if (f.pages.length > 1) { const pg = pageAt(f.pages, ctx.t - l.start); Object.assign(cap, { text: pg.text, tokens: pg.tokens, t0: pg.t0 }); }
    ctx.captionAt = cap;
  }

  /* ---------- chapter cards: the card holds, then tears away to the scene ---------- */
  const CARD = { hold: 1.1, tear: 0.7, lead: 2.2 };
  const cardW = new Map();
  function drawCard(i) {
    const seg = segs()[i]; if (!seg) return;
    const n = i + 1, C = KC();
    g.fillStyle = paperTile(C.kraft, 23, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
    paperRect(960, 548, 1780, 960, C.parchment, { seed: 900 + n, depth: 1.6, boil: false });
    Civic.bunting(140, 1780, 76, { sag: 40, n: 13, seed: 5 + n, sway: 0 });
    txt('CHAPTER', 960, 300, 52, { font: TYPE, color: C.navy });
    Civic.seal(960, 410, 80, { text: String(n), seed: 910 + n });
    const title = String(seg.chapter || seg.id).toUpperCase();
    let w0 = cardW.get(title);
    if (w0 == null) { w0 = ransom(title, -5000, -5000, 100, 1e9, { seed: 950 + n, palette: TITLE_PAL }); if (fontOk()) cardW.set(title, w0); }
    const size = Math.min(124, (1480 / Math.max(1, w0)) * 100);
    ransom(title, 960, 680, size, -1e6, { seed: 950 + n, palette: TITLE_PAL });
    hand('~ ' + String((Game.lesson && Game.lesson.title) || '').toLowerCase() + ' ~', 960, 880, 44, -1e6, { dur: 0.01, color: 'rgba(31,47,85,0.7)', seed: 960 + n });
  }
  function chapterSfx(part) { return part.kind === 'lines' ? [{ name: 'tear', t: CARD.hold, gain: 0.5 }] : []; }

  /* ======================================================================
     the numbers: the script's givens through the model (civics.js) and the bank evaluator
     ====================================================================== */
  let NUMS = null;
  function nums() {
    if (NUMS) return NUMS;
    const q3 = segById('s3').question, gv = q3.given, CH = Civics.CHAMBERS;
    if (gv.n !== CH.senate) console.warn('[video] q3 given n', gv.n, '≠ Civics.CHAMBERS.senate', CH.senate);
    const H = CH.house, hNeed = Civics.twoThirdsNeeded(H);
    const m = /Marbury v\. Madison, (\d{4})/.exec((Civics.RULES['judicial-review'] || {}).cite || '');
    NUMS = {
      house: H, senate: CH.senate,
      s3: { n: gv.n, y: gv.y, no: gv.no, need: Civics.twoThirdsNeeded(gv.n), rex: Math.floor(gv.n * 2 / 3), maj: Civics.majorityNeeded(gv.n), passes: Civics.overrides(gv.y, gv.no) },
      h: { n: H, need: hNeed, exact: (H * 2) % 3 === 0, eq: H * 2 / 3 },
      veto: Civics.VETOES,
      marbury: m ? m[1] : '',
    };
    return NUMS;
  }

  /* ======================================================================
     THE WORKING, STEP BY STEP (DESIGN.md §5.1, like lessons/percent-off): q3's `working` ({given, steps}) written on
     an index card as numbered steps in time with the Judge's reply, on the right AND both wrong branches, then the
     answer line (the correct option). Texts: Bank.fill; values: Bank.eval + the step's format ("dec2" two decimals).
     ====================================================================== */
  const FMT = (v, f) => (f === 'dec2' ? (Math.round(v * 100) / 100).toFixed(2) : Bank.format(v, f || 'text'));
  const STEP_LABEL = { 2: 'Check the vote' };   // a step whose text has no label of its own (q3 step 3: "{y} yes is short by")
  const WK = new Map();
  function working(q) {
    if (WK.has(q.id)) return WK.get(q.id);
    const W_ = q.working; if (!W_) { WK.set(q.id, null); return null; }
    const steps = W_.steps.map((st, i) => {
      const filled = Bank.fill(st.text, W_.given).trim(), v = Bank.eval(st.formula, W_.given), value = FMT(v, st.format);
      let label, maths; const k = filled.lastIndexOf(': ');
      if (k >= 0) { label = filled.slice(0, k); maths = filled.slice(k + 2); }
      else { const mm = /[\d$×−]/.exec(filled); label = mm && mm.index > 0 ? filled.slice(0, mm.index).trim() : ''; maths = mm ? filled.slice(mm.index) : filled; }
      if (!label) label = STEP_LABEL[i] || 'Check';
      return { i, n: i + 1, label, maths: maths + ' ' + value, text: filled + ' ' + value, value, v };
    });
    const opt = String(q.options[q.correct]), wk = { steps, answer: opt.includes(':') ? opt : 'Answer: ' + opt };
    WK.set(q.id, wk);
    return wk;
  }
  /* per branch, the reply word each step lands on (null = right after the previous one) */
  const STEPCUE = {
    q3: { q3_right: ['Two-thirds', 'part', 'need'], q3_wrong0: ['Two-thirds', 'and', 'short'], q3_wrong2: ['two-thirds', 'Sixty-six', 'under'] },
  };
  function stepTimes(c, q, wk, cardIn, hiCue) {
    const tbl = STEPCUE[q.id] || {}, out = [];
    let prev = cardIn;
    wk.steps.forEach((st, i) => {
      let cue = Infinity;
      for (const id in tbl) { const w = tbl[id][i]; if (w == null) { cue = Math.min(cue, c.L(id)); continue; } const [ww, nn] = Array.isArray(w) ? w : [w, 1]; cue = Math.min(cue, c.W(id, ww, nn)); }
      const t0 = Math.max(cue, prev + (i ? 0.75 : 0.25));
      out.push(t0); prev = t0;
    });
    const hi = Math.max(prev + 0.7, hiCue);
    return { steps: out, hi, answer: hi + 0.35, cardIn };
  }
  const WROW = { top: 84, pitch: 122 };
  function workCard(q, X, Y, W, tm) {
    const wk = working(q); if (!wk) return null;
    const n = wk.steps.length, H = WROW.top + n * WROW.pitch + 70;
    const s = pop(T, tm.cardIn, 0.3, 1.4);
    const geom = { h: H, rows: [] };
    if (s <= 0) return geom;
    g.save(); g.translate(X + W / 2, Y + H / 2); g.rotate(-0.008); g.scale(s, s);
    paperRect(0, 0, W, H, '#fffdf7', { seed: 560, depth: 1.4 });
    g.strokeStyle = 'rgba(90,150,190,0.28)'; g.lineWidth = 2; g.beginPath();
    for (let yy = -H / 2 + 62; yy < H / 2 - 8; yy += 61) { g.moveTo(-W / 2 + 14, yy); g.lineTo(W / 2 - 14, yy); }
    g.stroke(); g.strokeStyle = 'rgba(179,38,30,0.35)'; g.beginPath(); g.moveTo(-W / 2 + 60, -H / 2 + 10); g.lineTo(-W / 2 + 60, H / 2 - 10); g.stroke();
    tape(0, -H / 2 + 2, 140, 0.03, undefined, 561);
    g.restore();
    if (T < tm.cardIn + 0.2) return geom;
    txt('the working', X + W - 30, Y + 36, 28, { font: TYPE, color: PENCIL, align: 'right' });
    wk.steps.forEach((st, i) => {
      const t0 = tm.steps[i], ry = Y + WROW.top - 18 + i * WROW.pitch, my = ry + 58;
      hand(`${st.n}.` + (st.label ? ` ${st.label}` : ''), X + 76, ry, 30, t0, { align: 'left', color: PENCIL, font: TYPE, seed: 570 + i, dur: 0.3 });
      // the final value gets the highlighter (the last step)
      if (i === n - 1 && T > tm.hi) {
        const f = `56px ${HANDF}`, x0 = X + 96 + tw(st.maths.slice(0, st.maths.lastIndexOf(st.value)), f), vw = tw(st.value, f);
        g.save(); g.globalAlpha *= 0.45 * P(T, tm.hi, tm.hi + 0.3); g.fillStyle = '#f2c94c'; g.fillRect(x0 - 8, my - 26, vw + 16, 50); g.restore();
      }
      hand(st.maths, X + 96, my, 56, t0 + 0.2, { align: 'left', color: KC().navy, seed: 580 + i, dur: Math.min(0.9, 0.2 + st.maths.length * 0.03) });
      geom.rows[i] = { x: X + 96, y: my, maths: st.maths, value: st.value, t0 };
    });
    hand(wk.answer, X + 76, Y + H - 40, 50, tm.answer, { align: 'left', color: RED, seed: 590, dur: 0.4 });
    // QA: how many steps are written by now (every branch must show them all)
    const shown = tm.steps.filter(t0 => T >= t0 + 0.2).length;
    BranchesVideo.lastSteps = { q: q.id, n, shown, answer: T >= tm.answer, values: wk.steps.map(x => x.value) };
    if (UI.docked() && shown > 0) Dock.text('steps-' + q.id, wk.steps.filter((x, i) => T >= tm.steps[i] + 0.2).map(x => `Step ${x.n}: ${x.text}`).concat(T >= tm.answer ? [wk.answer] : []), { cls: 'note' });
    return geom;
  }

  /* ======================================================================
     the five scenes (BODY[id](t, part, ctx, c) draws the stage; S[id] wraps it with the chapter card)
     ====================================================================== */
  const BODY = {};

  /* ---- s1 "Decree Number One": the gym stage, the decree, one Rex, the three columns ---- */
  const S1 = { sx: 900, sy: 655, sw: 600, sh: 440, rex: [1335, 560], cols: [690, 960, 1230], colTop: 410, colW: 250, colH: 470, book: [1445, 800] };
  const DECREE = ['1. I write the rules.', '2. I enforce the rules.', '3. I judge who broke them.'];
  const decreeY = i => S1.sy - S1.sh / 2 + 175 + i * 88, decreeX = S1.sx - S1.sw / 2 + 50;
  function decree(t, tm) {
    const top = S1.sy - S1.sh / 2, C = KC();
    Civic.woodtype('DECREE No. 1', S1.sx, top + 74, 46, { color: C.navy, ink: C.cream, t0: tm.title, seed: 12 });
    DECREE.forEach((l, i) => {
      const y = decreeY(i), w = tw(l, `46px ${HANDF}`), cb = P(t, tm.col[i], tm.col[i] + 0.35);
      if (cb > 0) { g.save(); g.globalAlpha *= 0.4; g.fillStyle = Civic.BRANCH[BRS[i]].color; g.fillRect(decreeX - 12, y - 28, (w + 24) * E.outCubic(cb), 56); g.restore(); }
      hand(l, decreeX, y, 46, tm.line[i], { align: 'left', color: INK, seed: 20 + i, dur: 0.7 });
    });
    stampMark('TYRANNY', S1.sx + 40, top + 385, stampS(t, tm.tyr) * 0.8, { rot: -0.1 });
  }
  function podium(x, base) {
    if (IMG.podium) { cut('podium', x, base, 330, { ax: 0.5, ay: 1, seed: 71, depth: 1.3 }); return; }
    const C = KC();
    paperPiece(PODIUM, x, base, C.navy, { seed: 72, depth: 1.3 });
    paperRect(x, base - 272, 250, 26, '#7a4e2a', { seed: 73, depth: 0.6 });
    Civic.seal(x, base - 160, 40, { text: 'CR', seed: 74, ribbon: false });
  }
  const PODIUM = Object.assign([[-100, 0], [100, 0], [86, -250], [-86, -250]], { k: 'civic:podium' });
  function book(x, y, rot) {
    g.save(); g.translate(x, y); g.rotate(rot);
    paperRect(0, 0, 170, 230, '#5a3b26', { seed: 380, depth: 1.3 });
    paperRect(-72, 0, 18, 226, '#3f2819', { seed: 381, depth: 0 });
    paperRect(8, -58, 130, 4, '#c9a25a', { seed: 382, depth: 0 }); paperRect(8, 62, 130, 4, '#c9a25a', { seed: 383, depth: 0 });
    BOOK.lines.forEach((l, i) => txt(l, 8, -22 + i * 30, 26, { font: SERIF, color: '#f3ead7' }));
    txt(BOOK.year, 8, 90, 24, { font: TYPE, color: '#e0bf6a' });
    g.restore();
  }
  function column(k, t0, extras) {
    const s = pop(T, t0, 0.4, 1.8); if (s <= 0) return;
    const br = Civic.BRANCH[BRS[k]], W = S1.colW, H = S1.colH, x = S1.cols[k], C = KC();
    g.save(); g.translate(x, S1.colTop + H / 2); g.rotate((k - 1) * 0.012); g.scale(s, s);
    paperRect(0, 0, W, H, C.cream, { seed: 330 + k, depth: 1.3 });
    paperRect(0, -H / 2 + 36, W - 8, 64, br.color, { seed: 334 + k, depth: 0.2 });
    const ls = Math.min(30, 30 * (W - 30) / tw(br.label, `30px ${SLAB}`));
    txt(br.label, 0, -H / 2 + 38, ls, { font: SLAB, color: br.ink });
    Civic.tool(br.tool, 0, -H / 2 + 130, 96, { rot: 0.35, seed: 338 + k });
    let y = -H / 2 + 222;
    wrapLines(br.job, W - 36, 34).forEach((l, j) => { hand(l, 0, y, 34, t0 + 0.25 + j * 0.2, { color: INK, seed: 342 + k + j, dur: 0.35 }); y += 38; });
    extras.forEach(([str, te], j) => {
      if (T < te) return;
      y += 8;
      wrapLines(str, W - 44, 30).forEach((l, m) => { hand((m ? '' : '· ') + l, 0, y, 30, te + m * 0.2, { color: k === 2 ? GOLD_INK : br.color, seed: 350 + j * 3 + m, dur: 0.35 }); y += 34; });
    });
    g.restore();
  }
  BODY.s1 = function (t, part, ctx, c) {
    const C = KC(), q = part.seg.question, ans = ansOf(ctx, part);
    bgGym();
    Civic.bunting(-20, 980, 4, { sag: 46, n: 11, seed: 2 }); Civic.bunting(940, 1940, 4, { sag: 46, n: 11, seed: 3 });
    const tm = {
      un: c.W('s1_01', 'Decree'), title: c.W('s1_01', 'Number'),
      line: [c.W('s1_03', 'One'), c.W('s1_03', 'Two'), c.W('s1_03', 'Three')],
      col: [c.W('s1_04', 'make'), c.W('s1_04', 'carry'), c.W('s1_04', 'judge')], rex: c.W('s1_04', 'One'),
      book: c.W('s1_06', 'read'), tyr: c.W('s1_08', 'Tyranny'), split: c.W('s1_08', 'split'),
      job: [c.W('s1_08', 'makes'), c.W('s1_08', 'carries'), c.W('s1_08', 'judges')], boss: c.W('s1_09', 'boss'),
      extra: [c.W('s1_11', 'commands'), c.W('s1_11', 'deals')],
    };
    // the book prop drops in (s1_06): The Spirit of the Laws, 1748
    if (t > tm.book) {
      const land = tm.book + 0.45, u = P(t, tm.book, land), y = lerp(-240, S1.book[1], u * u) - 22 * Math.sin(Math.PI * P(t, land, land + 0.28));
      book(S1.book[0], y, 0.08 + (1 - u) * 0.5);
    }
    // the decree: unrolls on s1_01, one line per clause of s1_03, coloured on s1_04 … then tears into three strips (s1_08)
    const un = E.outCubic(P(t, tm.un, tm.un + 0.6));
    const drawScroll = () => scroll(S1.sx, S1.sy, S1.sw, S1.sh, un, () => decree(t, tm), 11);
    if (t < tm.split) {
      drawScroll();
      // "Three jobs. One Rex.": the three lines point at one small Rex
      if (t > tm.rex) {
        const s = pop(t, tm.rex, 0.35);
        Puppet.draw('rex', S1.rex[0], S1.rex[1] + 150, 240, { t, facing: -1, s });
        tag('ONE REX', S1.rex[0], S1.rex[1] + 185, 30, s, { font: SLAB, color: C.cream, ink: C.brick, seed: 44, rot: 0.05 });
        DECREE.forEach((l, i) => {
          const x1 = decreeX + tw(l, `46px ${HANDF}`) + 18, y1 = decreeY(i), a0 = tm.rex + 0.1 + i * 0.12;
          inkArrow(x1, y1, S1.rex[0] - 80, S1.rex[1] + 40 + (i - 1) * 40, { w: 6, color: Civic.BRANCH[BRS[i]].color, seed: 45 + i, prog: P(t, a0, a0 + 0.4), bulge: (i - 1) * 16, head: 20 });
        });
      }
    } else {
      // the scroll tears into three strips that fly to the three columns and become them
      const e = E.inOutCubic(P(t, tm.split, tm.split + 0.8)), sw3 = S1.sw / 3, top = S1.sy - S1.sh / 2;
      for (let k = 0; k < 3; k++) {
        const a = 1 - P(t, tm.job[k] - 0.05, tm.job[k] + 0.3); if (a <= 0) continue;
        const ox = S1.sx - S1.sw / 2 + sw3 * (k + 0.5), oy = S1.sy;
        const dx = (S1.cols[k] - ox) * e, dy = (S1.colTop + S1.colH / 2 - oy) * e - Math.sin(e * Math.PI) * 60;
        alpha(a, () => {
          g.save(); g.translate(ox + dx, oy + dy); g.rotate((k - 1) * 0.12 * Math.sin(e * Math.PI) + (k - 1) * 0.02); g.translate(-ox, -oy);
          g.beginPath(); g.rect(ox - sw3 / 2 - (k === 0 ? 40 : 0), top - 30, sw3 + (k === 0 || k === 2 ? 40 : 0), S1.sh + 60); g.clip();
          drawScroll(); g.restore();
        });
      }
    }
    // the three branches (s1_08), the executive's extra jobs (s1_11)
    for (let k = 0; k < 3; k++) column(k, tm.job[k], k === 1 ? [['commands the military', tm.extra[0]], ['deals with other countries', tm.extra[1]]] : []);
    // Rex: "the President's the boss of all three" (s1_09); struck in the reply
    const rs = replyStart(c, q);
    if (t > tm.boss) {
      const sb = pop(t, tm.boss, 0.35), by = S1.colTop - 44;
      inkArrow(S1.cols[1] - 110, by + 4, S1.cols[0] + 30, S1.colTop + 4, { w: 5, color: C.brick, seed: 361, prog: P(t, tm.boss + 0.2, tm.boss + 0.6), bulge: -14, head: 18 });
      inkArrow(S1.cols[1] + 110, by + 4, S1.cols[2] - 30, S1.colTop + 4, { w: 5, color: C.brick, seed: 362, prog: P(t, tm.boss + 0.3, tm.boss + 0.7), bulge: 14, head: 18 });
      tag('THE BOSS?', S1.cols[1], by, 34, sb, { font: SLAB, color: '#f2d479', ink: C.brick, seed: 360, rot: -0.04 });
      if (t > rs + 0.3) strike(S1.cols[1] - 120, by + 14, S1.cols[1] + 120, by - 14, P(t, rs + 0.3, rs + 0.65), 363);
    }
    // the reply (every branch): Congress makes the laws — the legislative column circled, "HOUSE + SENATE"
    const tCong = branch(c, { q1_right: 'Congress', q1_wrong0: 'Congress', q1_wrong2: 'Congress' });
    const tHS = branch(c, { q1_right: 'House', q1_wrong0: 'House', q1_wrong2: 'House' });
    if (t > tCong) inkCircle(S1.cols[0], S1.colTop + S1.colH / 2 - 4, 150, { sx: 0.95, sy: 1.62, w: 7, color: RED, seed: 364, prog: P(t, tCong, tCong + 0.5) });
    if (t > tHS) tag('HOUSE + SENATE', S1.cols[0], S1.colTop + S1.colH - 56, 30, pop(t, tHS, 0.35), { font: SLAB, color: C.navy, ink: C.cream, seed: 365, rot: 0.03 });
    // right answer only: "Article One, the very first article"
    const tArt = c.W('q1_right', 'Article');
    if (ans === q.correct && t > tArt) tag('ARTICLE I', S1.cols[0] - 170, S1.colTop + 140, 28, pop(t, tArt, 0.35), { font: SLAB, color: C.gold, ink: INK, seed: 366, rot: -0.2 });
    const st = stage(t, part, [[0, 'two']]);
    cast(t, ctx, st, { front: () => podium(st.rex.x + 40, 1088) });
  };
  S.s1 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.2, reply: 1.2, after: 1.8 }, gaps: { s1_08: 0.9 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, rs = replyStart(c, q);
      const out = chapterSfx(part);
      out.push({ name: fx('unroll', 'paper'), t: c.W('s1_01', 'Decree'), gain: 0.5 }, { name: 'pop', t: c.W('s1_01', 'Number'), gain: 0.25 });
      ['One', 'Two', 'Three'].forEach(w => out.push({ name: 'scribble', t: c.W('s1_03', w), gain: 0.2 }));
      ['make', 'carry', 'judge'].forEach(w => out.push({ name: 'pop', t: c.W('s1_04', w), gain: 0.2 }));
      out.push({ name: 'whoosh', t: c.W('s1_04', 'One'), gain: 0.25 }, { name: 'thunk', t: c.W('s1_06', 'read') + 0.45, gain: 0.45 });
      out.push({ name: 'stamp', t: c.W('s1_08', 'Tyranny'), gain: 0.5 }, { name: 'tear', t: c.W('s1_08', 'split'), gain: 0.5 });
      ['makes', 'carries', 'judges'].forEach(w => out.push({ name: 'pop', t: c.W('s1_08', w), gain: 0.3 }));
      out.push({ name: 'paper', t: c.W('s1_09', 'boss'), gain: 0.35 }, { name: 'scribble', t: rs + 0.3, gain: 0.3 });
      out.push({ name: 'scribble', t: branch(c, { q1_right: 'Congress', q1_wrong0: 'Congress', q1_wrong2: 'Congress' }), gain: 0.25 });
      out.push({ name: 'pop', t: branch(c, { q1_right: 'House', q1_wrong0: 'House', q1_wrong2: 'House' }), gain: 0.3 });
      out.push({ name: 'scribble', t: c.W('s1_11', 'commands'), gain: 0.18 }, { name: 'scribble', t: c.W('s1_11', 'deals'), gain: 0.18 });
      return out;
    },
  };

  /* ---- s2 "Make it, sign it": the flowchart, the bill's trip, the veto, the override path ---- */
  const S2 = { bx: [400, 820, 1240, 1640], by: 420, bw: 320, bh: 300, billY: 668, back: [620, 724] };
  const BOXES = [{ label: 'HOUSE', b: 'legislative', kind: 'capitol' }, { label: 'SENATE', b: 'legislative', kind: 'capitol' }, { label: 'PRESIDENT', b: 'executive', kind: 'whitehouse' }, { label: 'LAW', b: null, kind: 'law' }];
  function flowBox(k, s) {
    if (s <= 0.001) return;
    const B = BOXES[k], C = KC(), col = B.b ? Civic.BRANCH[B.b].color : C.wax;
    g.save(); g.translate(S2.bx[k], S2.by); g.rotate([-0.012, 0.01, -0.008, 0.012][k]); g.scale(s, s);
    paperRect(0, 0, S2.bw, S2.bh, C.cream, { seed: 420 + k, depth: 1.3 });
    paperRect(0, S2.bh / 2 - 36, S2.bw - 10, 62, col, { seed: 425 + k, depth: 0.2 });
    txt(B.label, 0, S2.bh / 2 - 34, 38, { font: SLAB, color: C.cream });
    if (B.kind === 'law') {
      if (IMG.scroll) cut('scroll', 0, -30, 170, { seed: 431, depth: 1 });
      else { Civic.parchment(0, -30, 190, 150, { seed: 432, depth: 0.8 }); for (let i = 0; i < 4; i++) ink([[-70, -80 + i * 28], [70, -80 + i * 28]], { w: 2, color: 'rgba(43,36,32,0.4)', seed: 433 + i, passes: 1, step: 40 }); }
      Civic.seal(78, 20, 38, { seed: 436 });
    } else Civic.building(B.kind, 0, 60, 160, { seed: 430 + k });
    g.restore();
  }
  function bill(x, y, s, o = {}) {
    if (s <= 0.001) return;
    const C = KC();
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.03); g.scale(s, s);
    paperRect(0, 0, 220, 118, C.parchment, { seed: 450, depth: 1.2 });
    paperRect(0, -60, 236, 20, '#d8c08e', { seed: 451, depth: 0.5, outline: true });
    paperRect(0, 60, 236, 20, '#d8c08e', { seed: 452, depth: 0.5, outline: true });
    BILL.forEach((l, i) => hand(l, 0, -16 + i * 34, 25, (o.tw ?? -1e6) + i * 0.3, { font: SLAB, color: C.navy, seed: 453 + i, dur: 0.4 }));
    if (o.note > 0) {
      g.save(); g.translate(96, 64); g.rotate(0.12); g.scale(o.note, o.note);
      paperRect(0, 0, 190, 50, '#fffdf7', { seed: 456, depth: 0.8, outline: true });
      txt('why: see attached', 0, 2, 24, { color: C.brick });
      ink([[-80, -30], [-80, -6]], { w: 5, color: '#8a8f99', seed: 457, passes: 1, step: 8 });
      g.restore();
    }
    stampMark('VETO', 0, 2, (o.veto || 0) * 0.66, { rot: -0.16 });
    g.restore();
  }
  BODY.s2 = function (t, part, ctx, c) {
    const C = KC(), q = part.seg.question, ans = ansOf(ctx, part), rs = replyStart(c, q), X = S2.bx;
    bgBoard();
    const tBox = [c.W('s2_02', 'House'), c.W('s2_02', 'Senate'), c.W('s2_04', 'President'), c.W('s2_04', 'boom')];
    tBox.forEach((t0, k) => {
      flowBox(k, pop(t, t0, 0.4, 1.8));
      if (k > 0) inkArrow(X[k - 1] + S2.bw / 2 + 6, S2.by, X[k] - S2.bw / 2 - 6, S2.by, { w: 7, color: INK, seed: 440 + k, prog: P(t, t0 - 0.1, t0 + 0.35), head: 22 });
    });
    // the members tags (Civics.CHAMBERS)
    const tHm = c.W('s2_03', 'four'), tSm = c.W('s2_03', 'one');
    if (t > tHm) tag(`${fmtInt(Civics.CHAMBERS.house)} members`, X[0] + 64, S2.by - S2.bh / 2 - 8, 30, pop(t, tHm, 0.35), { color: C.navy, ink: C.cream, seed: 460, rot: 0.05 });
    if (t > tSm) tag(`${fmtInt(Civics.CHAMBERS.senate)} members`, X[1] + 64, S2.by - S2.bh / 2 - 8, 30, pop(t, tSm, 0.35), { color: C.navy, ink: C.cream, seed: 461, rot: -0.04 });
    // the bill's trip: written (s2_01) → House → Senate (ticks, s2_03) → President (s2_04) → VETO, back to Congress (s2_05)
    const tW = c.W('s2_01', 'write'), tIn = c.L('s2_02'), tH = c.W('s2_03', 'passes'), tYes = c.W('s2_03', 'yes'), tNo = c.W('s2_03', 'no');
    const tP = c.W('s2_04', 'President'), tV = c.W('s2_05', 'veto'), tBack = c.W('s2_05', 'back'), tNote = c.W('s2_05', 'note');
    const tS = tYes + 0.2, tickS = Math.max(tNo + 0.3, tS + 0.7);
    const pos = track(t, [[-1e9, 800, 640, 1.6], [tIn - 0.1, 190, S2.billY, 1, 0.8], [tH, X[0], S2.billY, 1], [tS, X[1], S2.billY, 1], [tP, X[2], S2.billY, 1], [tBack, S2.back[0], S2.back[1], 1, 0.9]]);
    if (t > tYes) check(X[0] + 104, S2.by - 92, 0.9, P(t, tYes, tYes + 0.3), 470);
    if (t > tickS) check(X[1] + 104, S2.by - 92, 0.9, P(t, tickS, tickS + 0.3), 471);
    dashArrow(X[2], S2.by + S2.bh / 2 + 14, S2.back[0] + 150, S2.back[1] + 20, { bulge: -70, w: 5, color: C.brick, prog: P(t, tBack - 0.3, tBack + 0.5), seed: 472 });
    if (t > tW - 0.3) bill(pos.x, pos.y, pos.s * pop(t, tW - 0.3, 0.35), { tw: tW, veto: stampS(t, tV), note: pop(t, tNote, 0.3) });
    // the reply (every branch): two-thirds of each chamber, then on to LAW
    const tA = branch(c, { q2_right: 'two-thirds', q2_wrong0: 'Two-thirds', q2_wrong2: 'Two-thirds' });
    const tB = branch(c, { q2_right: 'Senate', q2_wrong0: 'Senate', q2_wrong2: 'chamber' });
    const tC = branch(c, { q2_right: 'law', q2_wrong0: 'override', q2_wrong2: 'override' });
    const bx = S2.back[0], byy = S2.back[1] - 60;
    inkArrow(bx - 60, byy, X[0] + 40, S2.by + S2.bh / 2 + 12, { w: 7, color: C.navy, seed: 480, prog: P(t, tA, tA + 0.45), bulge: 14, head: 22 });
    inkArrow(bx + 60, byy, X[1] - 40, S2.by + S2.bh / 2 + 12, { w: 7, color: C.navy, seed: 481, prog: P(t, tB, tB + 0.45), bulge: -14, head: 22 });
    if (t > tB) tag('2/3 + 2/3', bx, S2.by + S2.bh / 2 + 42, 32, pop(t, tB + 0.2, 0.35), { font: SLAB, color: C.navy, ink: C.cream, seed: 482, rot: -0.03 });
    inkArrow(X[1] + 20, S2.by - S2.bh / 2 - 36, X[3] - 20, S2.by - S2.bh / 2 - 12, { w: 7, color: C.navy, seed: 483, prog: P(t, tC, tC + 0.7), bulge: -120, head: 24 });
    if (t > tC + 0.3) hand('override = LAW', (X[1] + X[3]) / 2, S2.by - S2.bh / 2 - 118, 40, tC + 0.3, { color: C.navy, seed: 484, dur: 0.4 });
    // the misconception each wrong answer holds, struck (reply only)
    if (part.kind === 'reply' && ans === 0) sticky(['A VETO', 'IS FINAL'], 1470, 700, rs + 0.1, { w: 220, color: '#f4c6c0', seed: 490, strike: c.W('q2_wrong0', 'check'), rot: 0.05 });
    if (part.kind === 'reply' && ans === 2) sticky(['THE COURT', 'DECIDES'], 1470, 700, rs + 0.1, { w: 220, color: '#f4c6c0', seed: 491, strike: c.W('q2_wrong2', 'referee'), rot: 0.05 });
    // after: "Two-thirds is a lot."
    const tLot = c.W('s2_07', 'lot');
    if (t > tLot) inkCircle(bx, S2.by + S2.bh / 2 + 42, 110, { sx: 1, sy: 0.42, w: 6, color: RED, seed: 493, prog: P(t, tLot, tLot + 0.45) });
    const st = stage(t, part, part.kind === 'lines' ? [[0, 'two'], [tIn - 0.2, 'inset']] : [[0, 'inset']]);
    cast(t, ctx, st);
  };
  S.s2 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.2, reply: 1.6, after: 1.6 }, gaps: { s2_02: 1.0 } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      out.push({ name: 'scribble', t: c.W('s2_01', 'write'), gain: 0.25 }, { name: 'whoosh', t: c.L('s2_02') - 0.1, gain: 0.2 });
      [c.W('s2_02', 'House'), c.W('s2_02', 'Senate'), c.W('s2_04', 'President'), c.W('s2_04', 'boom')].forEach(t0 => out.push({ name: 'pop', t: t0, gain: 0.3 }));
      out.push({ name: 'paper', t: c.W('s2_03', 'four'), gain: 0.25 }, { name: 'paper', t: c.W('s2_03', 'one'), gain: 0.25 });
      const tYes = c.W('s2_03', 'yes');
      out.push({ name: 'whoosh', t: c.W('s2_03', 'passes'), gain: 0.18 }, { name: fx('tally', 'ding'), t: tYes, gain: 0.35 }, { name: fx('tally', 'ding'), t: Math.max(c.W('s2_03', 'no') + 0.3, tYes + 0.9), gain: 0.35 });
      out.push({ name: 'whoosh', t: c.W('s2_04', 'President'), gain: 0.18 }, { name: 'stamp', t: c.W('s2_05', 'veto'), gain: 0.55 }, { name: 'whoosh', t: c.W('s2_05', 'back'), gain: 0.22 }, { name: 'paper', t: c.W('s2_05', 'note'), gain: 0.3 });
      out.push({ name: 'scribble', t: branch(c, { q2_right: 'two-thirds', q2_wrong0: 'Two-thirds', q2_wrong2: 'Two-thirds' }), gain: 0.25 });
      out.push({ name: 'scribble', t: branch(c, { q2_right: 'Senate', q2_wrong0: 'Senate', q2_wrong2: 'chamber' }), gain: 0.25 });
      out.push({ name: 'scribble', t: branch(c, { q2_right: 'law', q2_wrong0: 'override', q2_wrong2: 'override' }), gain: 0.25 });
      out.push({ name: 'scribble', t: c.W('q2_wrong0', 'check'), gain: 0.3 }, { name: 'scribble', t: c.W('q2_wrong2', 'referee'), gain: 0.3 }, { name: 'scribble', t: c.W('s2_07', 'lot'), gain: 0.25 });
      return out;
    },
  };

  /* ---- s3 "Count the votes": the Senate board (66 / 34), the working, the House (290 of 435), the veto record ---- */
  const S3 = { card: { x: 600, y: 452, w: 620, h: 640 }, tile: 46, gap: 6, wk: { x: 1000, y: 150, w: 760 }, house: { x: 620, y: 440, w: 770, h: 470, tile: 20, gap: 4, cols: 29 }, veto: { x: 1392, y: 430, w: 640, h: 540 } };
  const NO_TILE = '#fbf5e6', UNVOTED = '#d6c7a6';
  function senateGrid(n) {
    const cols = 10, rows = Math.ceil(n / cols), pitch = S3.tile + S3.gap, bw = cols * pitch - S3.gap;
    return { cols, rows, tile: S3.tile, gap: S3.gap, pitch, x0: S3.card.x - bw / 2, y0: S3.card.y - S3.card.h / 2 + 32, bw };
  }
  const tileXY = (G, i) => [G.x0 + (i % G.cols) * G.pitch + G.tile / 2, G.y0 + Math.floor(i / G.cols) * G.pitch + G.tile / 2];
  /* the lower/right edge of the first k tiles (row-major) as a stepped line */
  function boundary(G, k) {
    const r = Math.floor(k / G.cols), cpos = k % G.cols;
    const X = cc => G.x0 + cc * G.pitch - G.gap / 2, Y = rr => G.y0 + rr * G.pitch - G.gap / 2;
    const L = G.x0 - 20, R = G.x0 + G.cols * G.pitch - G.gap + 20;
    if (cpos === 0) return { pts: [[L, Y(r)], [R, Y(r)]], y: Y(r), L, R };
    return { pts: [[L, Y(r + 1)], [X(cpos), Y(r + 1)], [X(cpos), Y(r)], [R, Y(r)]], y: Y(r + 1), L, R };
  }
  function senateBoard(t, n3, tm) {
    const C = KC(), G = senateGrid(n3.n), cd = S3.card;
    paperRect(cd.x, cd.y, cd.w, cd.h, C.parchment, { seed: 500, depth: 1.4 });
    let ky = 0, kn = 0;
    for (let i = 0; i < n3.n; i++) {
      const yes = i < n3.y, tf = yes ? lerp(tm.y0, tm.y1, i / Math.max(1, n3.y)) : lerp(tm.n0, tm.n1, (i - n3.y) / Math.max(1, n3.no));
      const u = P(t, tf, tf + 0.16), done = u >= 0.5, [x, y] = tileXY(G, i);
      if (done) { if (yes) ky++; else kn++; }
      const col = !done ? UNVOTED : yes ? C.navy : NO_TILE;
      paperRect(x, y, G.tile, G.tile, col, { seed: 501 + (i % 4) * 3 + (done ? (yes ? 1 : 2) : 0), depth: 0.35, sx: Math.max(0.06, Math.abs(Math.cos(Math.PI * u))), outline: done && !yes, boil: false });
    }
    if (t > tm.y0) {
      txt(`YES ${ky}`, cd.x - 150, cd.y + cd.h / 2 - 50, 50, { font: SLAB, color: C.navy });
      txt(`NO ${kn}`, cd.x + 150, cd.y + cd.h / 2 - 50, 50, { font: SLAB, color: INK });
    }
    return G;
  }
  function houseBoard(t, h, tFill0, tFill1, tLine) {
    const C = KC(), H = S3.house, pitch = H.tile + H.gap, rows = Math.ceil(h.n / H.cols), bw = H.cols * pitch - H.gap, bh = rows * pitch - H.gap;
    const G = { cols: H.cols, rows, tile: H.tile, gap: H.gap, pitch, x0: H.x - bw / 2, y0: H.y - H.h / 2 + 30, bw, bh };
    paperRect(H.x, H.y, H.w, H.h, C.parchment, { seed: 520, depth: 1.4 });
    const tiles = (col, key) => layer(key, G.x0 - 2, G.y0 - 2, bw + 4, bh + 4, () => {
      g.fillStyle = col; g.strokeStyle = 'rgba(43,36,32,0.35)'; g.lineWidth = 1.2;
      for (let i = 0; i < h.n; i++) { const x = G.x0 + (i % G.cols) * pitch, y = G.y0 + Math.floor(i / G.cols) * pitch; g.fillRect(x, y, H.tile, H.tile); g.strokeRect(x + 0.6, y + 0.6, H.tile - 1.2, H.tile - 1.2); }
    });
    tiles(NO_TILE, 'house_cream');
    const k = Math.round(h.need * E.inOutSine(P(t, tFill0, tFill1)));
    if (k > 0) {
      const r = Math.floor(k / G.cols), cc = k % G.cols;
      g.save(); g.beginPath(); g.rect(G.x0 - 2, G.y0 - 2, bw + 4, r * pitch); if (cc) g.rect(G.x0 - 2, G.y0 - 2 + r * pitch, cc * pitch, pitch); g.clip();
      tiles(C.navy, 'house_navy'); g.restore();
    }
    txt(`YES ${fmtInt(k)} of ${fmtInt(h.n)}`, H.x, H.y + H.h / 2 - 34, 40, { font: SLAB, color: C.navy });
    if (t > tLine) {
      const b = boundary(G, h.need);
      ink(b.pts, { w: 8, color: '#9a7420', seed: 530, prog: P(t, tLine, tLine + 0.5), step: 12, amp: 1 });
      tag(`${fmtInt(h.need)} needed`, b.L - 96, b.y, 32, pop(t, tLine + 0.3, 0.35), { font: SLAB, color: C.gold, ink: INK, seed: 531, rot: -0.05 });
      const eq = `${fmtInt(h.n)} × 2 ÷ 3 = ${fmtInt(h.eq)}${h.exact ? ', exactly' : ''}`;
      hand(eq, H.x, H.y + H.h / 2 + 50, 50, tLine + 0.4, { color: C.navy, seed: 532, dur: 0.8 });
    }
  }
  function vetoCard(t, V, tm) {
    const C = KC(), D = S3.veto;
    const e = P(t, tm.card, tm.card + 0.5), y = lerp(-420, D.y, E.outBack(e, 1.2));
    g.save(); g.translate(D.x, y); g.rotate(0.02 + (1 - e) * 0.3);
    texScrap('tex_news', 0, 0, D.w, D.h, { seed: 540, depth: 1.5, tint: C.cream, tintAlpha: 0.4 });
    paperRect(0, 12, D.w - 50, D.h - 70, '#fbf5e6', { seed: 541, depth: 0.4, boil: false });
    txt(`VETOES SINCE ${V.since}`, 0, -D.h / 2 + 80, 50, { font: SERIF, color: INK });
    ink([[-D.w / 2 + 50, -D.h / 2 + 118], [D.w / 2 - 50, -D.h / 2 + 118]], { w: 3, color: INK, seed: 542, passes: 1, step: 60 });
    const row = (big, label, yy, col, t0) => {
      const s = pop(t, t0, 0.35, 2); if (s <= 0) return;
      g.save(); g.translate(-D.w / 2 + 60, yy); g.scale(s, s);
      txt(big, 0, 0, 96, { font: SLAB, color: col, align: 'left' });
      g.restore();
      hand(label, -D.w / 2 + 80 + tw(big, `96px ${SLAB}`), yy + 12, 44, t0 + 0.2, { align: 'left', color: INK, seed: 543 + yy, dur: 0.4 });
    };
    row(fmtInt(V.total), 'vetoes', -80, C.brick, tm.total);
    row(fmtInt(V.overridden), 'overridden', 60, C.navy, tm.over);
    // the share as a bar: overridden / total of the width
    const bw = D.w - 120, bx = -bw / 2, byy = 124, pb = P(t, tm.bar, tm.bar + 0.6);
    if (pb > 0) {
      g.save(); g.globalAlpha *= Math.min(1, pb * 3);
      g.fillStyle = C.brick; g.fillRect(bx, byy - 14, bw, 28); g.fillStyle = C.navy; g.fillRect(bx, byy - 14, bw * (V.overridden / V.total) * E.outCubic(pb), 28);
      g.strokeStyle = INK; g.lineWidth = 2; g.strokeRect(bx, byy - 14, bw, 28); g.restore();
      hand(`about 1 in ${Math.round(V.total / V.overridden)} overridden`, 0, byy + 44, 38, tm.bar + 0.4, { color: INK, seed: 548, dur: 0.5 });
    }
    txt(`senate.gov veto table · read ${V.read}`, 0, D.h / 2 - 36, 20, { font: TYPE, color: PENCIL });
    stampMark('A CHECK', D.w / 2 - 110, 62, stampS(t, tm.check) * 0.5, { rot: 0.16, color: C.navy });
    g.restore();
  }
  BODY.s3 = function (t, part, ctx, c) {
    const C = KC(), q = part.seg.question, n3 = nums().s3, ans = ansOf(ctx, part);
    bgBoard();
    const tm = { y0: c.W('s3_02', 'Sixty-six'), y1: c.W('s3_02', 'Thirty-four') - 0.1, n0: c.W('s3_02', 'Thirty-four'), n1: c.E('s3_02') + 0.3 };
    const hiCue = branch(c, { q3_right: 'sixty-seven', q3_wrong0: 'sixty-seven', q3_wrong2: 'sixty-seven' });
    const rs = replyStart(c, q), wt = stepTimes(c, q, working(q), rs + 0.4, hiCue);
    const tSw = c.L('s3_04') - 0.2, out = E.inOutCubic(P(t, tSw, tSw + 0.6)), inn = E.outCubic(P(t, tSw + 0.2, tSw + 0.8));
    // the Senate: header, board, Rex's claim, the working (slides away for the House on s3_04)
    if (out < 1) alpha(1 - out, () => {
      g.save(); g.translate(-700 * out, 0);
      Civic.woodtype('SENATE · OVERRIDE VOTE', S3.card.x, 86, 40, { color: C.navy, ink: C.cream, t0: part.kind === 'lines' ? CARD.hold + CARD.tear : -1e6, seed: 510 });
      const G = senateBoard(t, n3, tm);
      // Rex: "two-thirds of a hundred is sixty-six" (s3_01), struck in the reply
      const tRex = c.W('s3_01', 'sixty-six');
      alpha(1 - P(t, wt.cardIn - 0.2, wt.cardIn + 0.2), () => sticky([`2/3 of ${n3.n}`, `= ${n3.rex}. Easy!`], 1340, 330, tRex, { w: 280, seed: 512, strike: rs + 0.25, rot: 0.05 }));
      // wrong 2 ("a majority"): the majority line for contrast
      if (ans === 2 && part.kind !== 'lines') {
        const tMj = c.W('q3_wrong2', 'majority');
        if (t > tMj) { const b = boundary(G, n3.maj); dashArrow(b.L, b.y, b.R, b.y, { w: 5, color: C.navy, prog: P(t, tMj, tMj + 0.4), seed: 513 }); tag(`majority: ${n3.maj}`, b.L - 90, b.y - 16, 28, pop(t, tMj + 0.2, 0.35), { font: SLAB, color: C.navy, ink: C.cream, seed: 514, rot: -0.05 }); }
      }
      // the reply: the line at 67, the 67th tile blinks empty, the verdict (every branch)
      if (t > wt.hi) {
        const b = boundary(G, n3.need), [x, y] = tileXY(G, n3.need - 1);
        if (Math.sin((t - wt.hi) * 9) > -0.2) { g.save(); g.fillStyle = KC().parchment; g.fillRect(x - 25, y - 25, 50, 50); g.setLineDash([8, 6]); g.lineWidth = 5; g.strokeStyle = '#9a7420'; g.strokeRect(x - 21, y - 21, 42, 42); g.restore(); }
        ink(b.pts, { w: 8, color: '#9a7420', seed: 515, prog: P(t, wt.hi, wt.hi + 0.5), step: 12, amp: 1 });
        tag(`${n3.need} needed`, b.L - 96, b.y, 32, pop(t, wt.hi + 0.3, 0.35), { font: SLAB, color: C.gold, ink: INK, seed: 516, rot: -0.05 });
        stampMark(n3.passes ? 'PASSES' : 'FAILS', S3.card.x + 110, S3.card.y + 160, stampS(t, wt.hi + 0.7), { rot: -0.14, size: 72 });
      }
      const Gk = workCard(q, S3.wk.x, S3.wk.y, S3.wk.w, wt);
      // after: "One vote!" — the 1 gets a red circle
      const tOne = c.W('s3_03', 'One');
      if (Gk && Gk.rows[2] && t > tOne) { const r = Gk.rows[2], f = `56px ${HANDF}`, vx = r.x + tw(r.maths, f) - tw(r.value, f) / 2; inkCircle(vx, r.y, 38, { w: 6, color: RED, seed: 517, prog: P(t, tOne, tOne + 0.4) }); }
      g.restore();
    });
    // the House (s3_04): 435 tiles, the line at 290, exactly two-thirds
    if (inn > 0) alpha(inn, () => {
      g.save(); g.translate(900 * (1 - inn), 0);
      const h = nums().h;
      Civic.woodtype(`HOUSE · ALL ${fmtInt(h.n)} VOTING`, S3.house.x, 86, 40, { color: C.navy, ink: C.cream, t0: tSw + 0.4, seed: 521 });
      houseBoard(t, h, c.W('s3_04', 'two-thirds'), c.W('s3_04', 'ninety'), c.W('s3_04', 'exactly'));
      g.restore();
    });
    // the real veto record (s3_06), facts 4.7 through Civics.VETOES
    const V = nums().veto, tCard = c.L('s3_06');
    if (t > tCard) {
      vetoCard(t, V, { card: tCard, total: c.W('s3_06', 'two'), over: c.W('s3_06', 'overrode'), bar: c.W('s3_06', 'twelve'), check: c.W('s3_08', 'check') });
      if (UI.docked()) Dock.text('vetoes', [`Vetoes since ${V.since}: ${fmtInt(V.total)}. Overridden: ${fmtInt(V.overridden)} (about 1 in ${Math.round(V.total / V.overridden)}).`, `senate.gov veto table, read ${V.read}`], { cls: 'note' });
    }
    const st = stage(t, part, [[0, 'inset']]);
    cast(t, ctx, st);
  };
  S.s3 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 2.4, after: 1.8 }, gaps: { s3_04: 1.2, s3_06: 1.0 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, n3 = nums().s3, out = chapterSfx(part);
      const y0 = c.W('s3_02', 'Sixty-six'), y1 = c.W('s3_02', 'Thirty-four') - 0.1, n0 = c.W('s3_02', 'Thirty-four'), n1 = c.E('s3_02') + 0.3;
      out.push({ name: 'pop', t: c.W('s3_01', 'sixty-six'), gain: 0.3 });
      if (Audio.has('drumroll')) out.push({ name: 'drumroll', t: y0 - 0.2, gain: 0.3, loop: true, dur: Math.max(0.5, n1 - y0 + 0.3) });
      for (let i = 0; i < n3.n; i += 4) { const tf = i < n3.y ? lerp(y0, y1, i / n3.y) : lerp(n0, n1, (i - n3.y) / n3.no); out.push({ name: fx('tally', 'pop'), t: tf, gain: 0.12 }); }
      const hiCue = branch(c, { q3_right: 'sixty-seven', q3_wrong0: 'sixty-seven', q3_wrong2: 'sixty-seven' }), rs = replyStart(c, q), wt = stepTimes(c, q, working(q), rs + 0.4, hiCue);
      out.push({ name: 'scribble', t: rs + 0.25, gain: 0.3 }, { name: 'paper', t: wt.cardIn, gain: 0.3 }, ...wt.steps.map(t0 => ({ name: 'scribble', t: t0 + 0.2, gain: 0.16 })));
      out.push({ name: 'scribble', t: wt.hi, gain: 0.2 }, { name: 'stamp', t: wt.hi + 0.7, gain: 0.5 }, { name: 'scribble', t: wt.answer, gain: 0.18 });
      out.push({ name: 'scribble', t: c.W('q3_wrong2', 'majority'), gain: 0.2 }, { name: 'scribble', t: c.W('s3_03', 'One'), gain: 0.25 });
      out.push({ name: 'whoosh', t: c.L('s3_04') - 0.2, gain: 0.25 }, { name: 'scribble', t: c.W('s3_04', 'exactly'), gain: 0.2 });
      out.push({ name: 'whoosh', t: c.L('s3_06'), gain: 0.2 }, { name: 'thunk', t: c.L('s3_06') + 0.45, gain: 0.4 }, { name: 'pop', t: c.W('s3_06', 'two'), gain: 0.3 }, { name: 'pop', t: c.W('s3_06', 'overrode'), gain: 0.3 });
      out.push({ name: 'stamp', t: c.W('s3_08', 'check'), gain: 0.4 });
      return out;
    },
  };

  /* ---- s4 "Ask the courts": the law and Rex's red line, the case, the Court, the gavel; Marbury; courts wait ---- */
  const S4 = { sc: { x: 610, y: 440, w: 600, h: 560 }, redY: 580, court: { x: 1400, base: 470, h: 250 }, chairsY: 546, file: { x: 1400, y: 690 }, card: { x: 1330, y: 410, w: 820, h: 480 }, tray: { x: 1300, y: 600 } };
  const RED_LINE = 'Anyone who complains loses recess.';
  function lawText(t, tm) {
    const C = KC(), sc = S4.sc, top = sc.y - sc.h / 2, x0 = sc.x - sc.w / 2 + 50;
    txt(BILL[0], sc.x, top + 70, 42, { font: SLAB, color: C.navy }); txt(BILL[1], sc.x, top + 118, 42, { font: SLAB, color: C.navy });
    ink([[x0, top + 158], [sc.x + sc.w / 2 - 50, top + 158]], { w: 3, color: INK, seed: 602, passes: 1, step: 60 });
    hand('Sec. 1. No homework on Fridays.', x0, top + 220, 38, -1e6, { align: 'left', dur: 0.01, color: INK, seed: 603 });
    hand('Sec. 2. Forever.', x0, top + 276, 38, -1e6, { align: 'left', dur: 0.01, color: INK, seed: 604 });
    if (t < tm.tear) hand(RED_LINE, x0, S4.redY, 38, tm.red, { align: 'left', color: RED, seed: 605, dur: 1.1 });
  }
  function chair(x, y, s) {
    if (s <= 0.001) return;
    g.save(); g.translate(x, y); g.scale(s, s);
    paperRect(0, -26, 30, 40, '#7a4e2a', { seed: 620, depth: 0.5 });
    paperRect(0, 0, 40, 10, '#5d3a1f', { seed: 621, depth: 0.4 });
    ink([[-16, 4], [-16, 24]], { w: 3, color: '#3f2819', seed: 622, passes: 1, step: 8 }); ink([[16, 4], [16, 24]], { w: 3, color: '#3f2819', seed: 623, passes: 1, step: 8 });
    g.restore();
  }
  function caseFile(x, y) {
    const C = KC();
    g.save(); g.translate(x, y); g.rotate(-0.03);
    paperRect(-150, -80, 150, 30, '#d9aa62', { seed: 630, depth: 0.5 });
    paperRect(0, 0, 480, 150, '#e0b870', { seed: 631, depth: 1.3 });
    txt('STUDENTS v.', 0, -30, 30, { font: TYPE, color: INK });
    txt('THE NO HOMEWORK FRIDAYS ACT', 0, 18, 26, { font: TYPE, color: INK });
    txt('CASE FILE', 170, -58, 18, { font: TYPE, color: C.brick });
    g.restore();
  }
  function marburyCard(t, tm, year) {
    const C = KC(), D = S4.card, e = P(t, tm.card, tm.card + 0.5), gone = E.inOutCubic(P(t, tm.away, tm.away + 0.5));
    if (e <= 0 || gone >= 1) return;
    const y = lerp(-400, D.y, E.outBack(e, 1.1)) - gone * 700;
    g.save(); g.globalAlpha *= 1 - gone; g.translate(D.x, y); g.rotate(-0.015 + (1 - e) * 0.25);
    paperRect(0, 0, D.w, D.h, '#fbf3df', { seed: 640, depth: 1.5 });
    paperRect(-D.w / 2 + 14, 0, 16, D.h - 20, C.gold, { seed: 641, depth: 0 });
    txt(MARBURY.title, -D.w / 2 + 52, -D.h / 2 + 68, 54, { font: SERIF, color: C.navy, align: 'left' });
    if (year) tag(year, D.w / 2 - 90, -D.h / 2 + 64, 40, 1, { font: SLAB, color: C.gold, ink: INK, seed: 642, rot: 0.06 });
    txt(MARBURY.who, -D.w / 2 + 52, -D.h / 2 + 142, 28, { font: TYPE, color: INK, align: 'left' });
    const lines = wrapLines('"' + MARBURY.quote + '"', D.w - 120, 50);
    lines.forEach((l, i) => hand(l, -D.w / 2 + 60, -D.h / 2 + 212 + i * 62, 50, tm.quote + i * 0.9, { align: 'left', color: INK, seed: 643 + i, dur: 0.9 }));
    txt(MARBURY.small, -D.w / 2 + 52, D.h / 2 - 34, 22, { font: TYPE, color: PENCIL, align: 'left' });
    g.restore();
  }
  BODY.s4 = function (t, part, ctx, c) {
    const C = KC(), q = part.seg.question, rs = replyStart(c, q), sc = S4.sc;
    bgCourt();
    const tm = {
      law: c.W('s4_01', 'law'), red: c.W('s4_01', 'Anyone'), court: c.W('s4_02', 'court'), file: c.W('s4_02', 'Somebody'), declare: c.W('s4_03', 'declare'),
      tear: branch(c, { q4_right: 'struck', q4_wrong0: 'courts', q4_wrong2: 'courts' }),
      card: c.W('s4_04', 'Marbury'), quote: c.W('s4_06', 'Marshall'), away: c.W('s4_08', 'wait'), drop: c.W('s4_08', 'Somebody'),
    };
    // the law: the scroll with its seal (s4_01) and Rex's red line — torn off when the gavel comes down (the reply)
    scroll(sc.x, sc.y, sc.w, sc.h, 1, () => lawText(t, tm), 600);
    Civic.seal(sc.x + 200, sc.y + sc.h / 2 - 76, 56, { text: 'LAW', seed: 606, s: pop(t, tm.law, 0.35, 2.6), rot: -0.12 });
    if (t > tm.tear) {
      paperRect(sc.x, S4.redY, sc.w - 24, 66, '#cdbb94', { seed: 607, torn: true, depth: 0, boil: false, outline: true });
      const e = P(t, tm.tear, tm.tear + 1.1), fall = e * e;
      alpha(1 - P(e, 0.75, 1), () => {
        g.save(); g.translate(sc.x + 60 * fall, S4.redY + 420 * fall); g.rotate(0.35 * fall + 0.04 * Math.sin(e * 12));
        paperRect(0, 0, sc.w - 30, 66, KC().parchment, { seed: 608, torn: true, depth: 1.2, boil: false });
        hand(RED_LINE, -sc.w / 2 + 50, 0, 38, -1e6, { align: 'left', color: RED, seed: 605, dur: 0.01 });
        g.restore();
      });
      stampMark('STRUCK DOWN', sc.x, S4.redY - 6, stampS(t, tm.tear + 0.4) * 0.72, { rot: -0.08 });
    }
    // the gavel comes down on the red line (every branch), then rests; beside the in-tray at the end (s4_08)
    if (t > tm.tear - 0.7 && t < tm.away + 0.2) {
      // pivot at the handle's end: raised (head up) → down onto the red line on the cue → a small bounce, then it lies there
      const down = E.inCubic(P(t, tm.tear - 0.35, tm.tear)), bounce = Math.sin(Math.PI * P(t, tm.tear, tm.tear + 0.35)) * 0.3;
      const a = lerp(-0.25, -1.5, down) + bounce, px = sc.x + 330, py = S4.redY + 8;
      alpha(P(t, tm.tear - 0.7, tm.tear - 0.45) * (1 - P(t, tm.away - 0.2, tm.away + 0.2)), () => {
        g.save(); g.translate(px, py); g.rotate(a); Civic.tool('gavel', 0, -62, 170, { seed: 609 }); g.restore();
      });
    }
    // the case, the Court rising at the back, nine empty chairs (s4_02) — they give way to the Marbury card
    const bye = 1 - P(t, tm.card, tm.card + 0.4);
    if (bye > 0) alpha(bye, () => {
      const e = E.outCubic(P(t, tm.court, tm.court + 0.8));
      if (e > 0) alpha(e, () => Civic.building('court', S4.court.x, S4.court.base + (1 - e) * 140, S4.court.h, { seed: 611 }));
      for (let i = 0; i < JUSTICES; i++) chair(S4.court.x + (i - (JUSTICES - 1) / 2) * 50, S4.chairsY, pop(t, tm.court + 0.6 + i * 0.07, 0.3));
      const fx_ = E.outCubic(P(t, tm.file, tm.file + 0.5));
      if (fx_ > 0) caseFile(lerp(2250, S4.file.x, fx_), S4.file.y);
    });
    // Rex: "I'll just declare my law constitutional" (s4_03) — struck in the reply
    alpha(1 - P(t, c.L('s4_04') - 0.2, c.L('s4_04') + 0.2), () => sticky(['DECLARED', 'CONSTITUTIONAL.', '— REX'], 1050, 250, tm.declare, { w: 250, size: 30, color: '#f4c6c0', seed: 612, strike: rs + 0.3, rot: 0.06 }));
    // after: Marbury v. Madison (s4_04) with Marshall's line (s4_06)
    marburyCard(t, tm, nums().marbury);
    if (UI.docked() && t > tm.quote && t < tm.away) Dock.text('marbury', [`${MARBURY.title}${nums().marbury ? ', ' + nums().marbury : ''}`, `${MARBURY.who} "${MARBURY.quote}"`], { cls: 'note' });
    // after: courts wait for a real case (s4_08): the in-tray, the gavel beside it, a folder drops in
    const tTray = tm.away + 0.2;
    if (t > tTray) {
      const s = pop(t, tTray, 0.4);
      const dy = lerp(-300, 0, E.outCubic(P(t, tm.drop, tm.drop + 0.45)));
      if (t > tm.drop) { g.save(); g.translate(S4.tray.x, S4.tray.y - 30 + dy); g.rotate(-0.04); paperRect(0, 0, 300, 44, '#e0b870', { seed: 650, depth: 0.8 }); g.restore(); }
      g.save(); g.translate(S4.tray.x, S4.tray.y); g.scale(s, s);
      paperRect(0, 20, 380, 90, '#b98a58', { seed: 651, depth: 1.3, outline: true });
      txt('CASES', 0, 22, 42, { font: SLAB, color: C.cream });
      g.restore();
      Civic.tool('gavel', S4.tray.x + 290, S4.tray.y + 10, 150, { rot: 0.5 - 0.35 * E.outBack(P(t, tm.drop + 0.6, tm.drop + 1.0)), seed: 652, s });
    }
    const st = stage(t, part, [[0, 'inset']]);
    cast(t, ctx, st);
  };
  S.s4 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.2, reply: 2.2, after: 1.8 }, gaps: { s4_04: 0.8, s4_08: 0.8 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part), tear = branch(c, { q4_right: 'struck', q4_wrong0: 'courts', q4_wrong2: 'courts' });
      out.push({ name: 'stamp', t: c.W('s4_01', 'law'), gain: 0.45 }, { name: 'scribble', t: c.W('s4_01', 'Anyone'), gain: 0.3 });
      out.push({ name: 'whoosh', t: c.W('s4_02', 'court'), gain: 0.25 }, { name: 'paper', t: c.W('s4_02', 'Somebody'), gain: 0.35 }, { name: 'pop', t: c.W('s4_03', 'declare'), gain: 0.3 });
      out.push({ name: 'scribble', t: replyStart(c, q) + 0.3, gain: 0.3 }, { name: fx('gavel', 'thunk'), t: tear - 0.05, gain: 0.6 }, { name: 'tear', t: tear + 0.05, gain: 0.5 }, { name: 'stamp', t: tear + 0.4, gain: 0.45 });
      out.push({ name: 'paper', t: c.W('s4_04', 'Marbury'), gain: 0.4 }, { name: 'scribble', t: c.W('s4_06', 'Marshall'), gain: 0.2 });
      out.push({ name: 'whoosh', t: c.W('s4_08', 'wait'), gain: 0.2 }, { name: 'thunk', t: c.W('s4_08', 'Somebody') + 0.45, gain: 0.4 });
      return out;
    },
  };

  /* ---- s5 "Nobody gets all the power": the checks web, the rule of law, Rex's constitution, SIGNED ---- */
  const NODE = { legislative: { x: 960, base: 250, h: 170 }, executive: { x: 320, base: 700, h: 150 }, judicial: { x: 1600, base: 700, h: 160 } };
  /* every check, drawn in the colour of the branch that HOLDS it (by). The arrows along one side of the triangle run
     parallel (offset k along the side's outer normal) and their labels step down the side (lt), so no two labels meet.
     old = already met in earlier chapters (faint from the start); cue = the word that draws it. */
  const SIDE = {
    left: { a: [410, 585], b: [850, 228], out: -1 },      // executive ↔ Congress (a = the executive end)
    right: { a: [1510, 585], b: [1070, 228], out: 1 },    // courts ↔ Congress (a = the courts end)
  };
  const CHK = [
    { side: 'left', k: 2, dir: 1, by: 'executive', lab: 'veto', lt: 0.74, old: true },
    { side: 'left', k: 1, dir: -1, by: 'legislative', lab: 'override 2/3', lt: 0.63, old: true },
    { side: 'left', k: 0, dir: -1, by: 'legislative', lab: 'confirms', lt: 0.52, cue: ['s5_02', 'confirm'], d: 0.15 },
    { side: 'left', k: -1, dir: -1, by: 'legislative', lab: 'the budget', lt: 0.41, cue: ['s5_02', 'budget'] },
    { side: 'left', k: -2, dir: -1, by: 'legislative', lab: 'impeach', lt: 0.3, cue: ['s5_03', 'impeach'] },
    { side: 'right', k: 1, dir: 1, by: 'judicial', lab: 'strike down', lt: 0.7, old: true },
    { side: 'right', k: 0, dir: -1, by: 'legislative', lab: 'confirms', lt: 0.5, cue: ['s5_02', 'confirm'] },
    { side: 'right', k: -1, dir: -1, by: 'legislative', lab: 'impeach', lt: 0.3, cue: ['s5_03', 'impeach'], d: 0.15 },
    { bottom: true, y: 640, dir: 1, by: 'executive', lab: 'nominates judges', lt: 0.5, cue: ['s5_02', 'nominates'], ly: -30 },
    { bottom: true, y: 712, dir: -1, by: 'judicial', lab: 'strike down', lt: 0.5, old: true, ly: 26 },
  ];
  /* an arrow's end points (from → to) and its label point */
  function chkGeom(k) {
    if (k.bottom) {
      const A = [430, k.y], B = [1490, k.y], from = k.dir > 0 ? A : B, to = k.dir > 0 ? B : A;
      return { from, to, lab: [lerp(A[0], B[0], k.lt), k.y + (k.ly || 0)] };
    }
    const Sd = SIDE[k.side], dx = Sd.b[0] - Sd.a[0], dy = Sd.b[1] - Sd.a[1], L = Math.hypot(dx, dy);
    // the side's normal pointing out of the triangle (up-left on the left side, up-right on the right side)
    const nx = Sd.out < 0 ? dy / L : -dy / L, ny = Sd.out < 0 ? -dx / L : dx / L;
    const o = k.k * 46, A = [Sd.a[0] + nx * o, Sd.a[1] + ny * o], B = [Sd.b[0] + nx * o, Sd.b[1] + ny * o];
    const from = k.dir > 0 ? A : B, to = k.dir > 0 ? B : A;
    return { from, to, lab: [lerp(A[0], B[0], k.lt), lerp(A[1], B[1], k.lt)] };
  }
  const ink4 = by => (by === 'judicial' ? GOLD_INK : Civic.BRANCH[by].color);
  function web(t, c, tm) {
    const C = KC();
    // the three branches (the buildings pop in as the chapter card tears away)
    BRS.forEach((b, k) => {
      const N = NODE[b], br = Civic.BRANCH[b], s = pop(t, CARD.hold + 0.2 + k * 0.15, 0.4, 1.8);
      Civic.building(br.building, N.x, N.base, N.h, { s, seed: 700 + k });
      const name = b === 'legislative' ? 'CONGRESS' : b === 'executive' ? 'PRESIDENT' : 'COURTS';
      if (s > 0) tag(name, N.x, N.base + 34, 30, s, { font: SLAB, color: br.color, ink: br.ink, seed: 703 + k, rot: 0 });
    });
    const s0 = pop(t, CARD.hold + 0.6, 0.35);
    tag('HOUSE', 730, 122, 26, s0, { font: SLAB, color: C.cream, ink: C.navy, seed: 706, rot: -0.06 });
    tag('SENATE', 1190, 122, 26, s0, { font: SLAB, color: C.cream, ink: C.navy, seed: 707, rot: 0.06 });
    // the checks: the faint ones are already there; the new ones draw on in time with the words
    CHK.forEach((k, i) => {
      const t0 = k.old ? CARD.hold + 0.8 + i * 0.1 : c.W(k.cue[0], k.cue[1]) + (k.d || 0);
      const pr = P(t, t0, t0 + 0.5); if (pr <= 0) return;
      const G = chkGeom(k), col = ink4(k.by);
      alpha(k.old ? 0.5 : 1, () => {
        inkArrow(G.from[0], G.from[1], G.to[0], G.to[1], { w: k.old ? 5 : 7, color: col, seed: 720 + i, prog: pr, head: k.old ? 18 : 24 });
        if (pr > 0.5) tag(k.lab, G.lab[0], G.lab[1], k.old ? 27 : 30, pop(t, t0 + 0.25, 0.3), { color: '#fffdf7', ink: col, seed: 740 + i, rot: 0, depth: 0.5 });
      });
    });
    // "the Senate holds the trial. Removing takes two-thirds."
    if (t > tm.trial) tag('Senate trial: 2/3 to remove', 1470, 110, 30, pop(t, tm.trial, 0.35), { color: '#fffdf7', ink: C.navy, seed: 760, rot: 0.03 });
    // the rule of law (s5_05)
    const rb = E.outBack(P(t, tm.rule, tm.rule + 0.4), 1.4);
    if (rb > 0) {
      g.save(); g.translate(960, 516); g.rotate(-0.015); g.scale(rb, 1);
      paperPiece(RIB_L, -530, 0, '#7e211c', { seed: 761, depth: 0.8 }); paperPiece(RIB_R, 530, 0, '#7e211c', { seed: 762, depth: 0.8 });
      paperRect(0, 0, 1060, 86, C.brick, { seed: 763, depth: 1.2 });
      txt('RULE OF LAW: NO ONE IS ABOVE IT', 0, 4, 44, { font: SLAB, color: C.cream });
      g.restore();
    }
  }
  const RIB_L = Object.assign([[40, -36], [-70, -36], [-40, 0], [-70, 36], [40, 36]], { k: 'civic:ribL' });
  const RIB_R = Object.assign([[-40, -36], [70, -36], [40, 0], [70, 36], [-40, 36]], { k: 'civic:ribR' });
  const COUNCIL = ['1. Class council makes the rules.', '2. The president carries them out.', '3. A student court judges.'];
  BODY.s5 = function (t, part, ctx, c) {
    const C = KC();
    bgParch();
    const tm = { trial: c.W('s5_03', 'trial'), rule: c.W('s5_05', 'rule'), shr: c.L('s5_06') - 0.3, lines: [c.W('s5_06', 'council'), c.W('s5_06', 'carry'), c.W('s5_06', 'court')], sign: c.W('s5_07', 'Sign'), signed: c.W('s5_08', 'Signed'), gavel: c.W('s5_08', 'gavel') };
    const e = E.inOutCubic(P(t, tm.shr, tm.shr + 0.9));
    // Rex's constitution (s5_06): the parchment unrolls, three lines in the three branch colours, then SIGNED (s5_08)
    const un = E.outCubic(P(t, tm.shr + 0.35, tm.shr + 1.0)), PX = 960, PY = 612, PW = 820, PH = 560;
    if (un > 0) scroll(PX, PY, PW, PH, un, () => {
      const top = PY - PH / 2;
      txt('CORAL RIDGE COUNCIL', PX, top + 72, 46, { font: SLAB, color: C.navy });
      ink([[PX - 330, top + 112], [PX + 330, top + 112]], { w: 3, color: INK, seed: 780, passes: 1, step: 60 });
      COUNCIL.forEach((l, i) => {
        const y = top + 176 + i * 80, br = Civic.BRANCH[BRS[i]];
        if (t > tm.lines[i]) paperRect(PX - 350, y, 26, 26, br.color, { seed: 781 + i, depth: 0.3, s: pop(t, tm.lines[i], 0.3) });
        hand(l, PX - 318, y, 44, tm.lines[i], { align: 'left', color: INK, seed: 784 + i, dur: 0.8 });
      });
      const sy = top + PH - 80;
      hand('signed:', PX - 330, sy - 6, 34, -1e6, { align: 'left', dur: 0.01, color: PENCIL, seed: 787 });
      ink([[PX - 210, sy + 8], [PX + 200, sy + 8]], { w: 3, color: INK, seed: 788, passes: 1, step: 60 });
      const sig = []; for (let i = 0; i <= 36; i++) { const u = i / 36; sig.push([PX - 190 + u * 360, sy - 14 + Math.sin(u * 26) * 16 * (1 - u * 0.5) - u * 10]); }
      ink(sig, { w: 5, color: C.navy, seed: 789, prog: P(t, tm.signed, tm.signed + 0.6), step: 6, passes: 1 });
    }, 790);
    // the web: full size, then it shrinks into Rex's parchment (and is gone as the parchment opens)
    if (e < 1) alpha(1 - P(e, 0.55, 1), () => {
      const sc = lerp(1, 0.1, e);
      g.save(); g.translate(lerp(960, PX, e), lerp(440, PY, e)); g.scale(sc, sc); g.translate(-960, -440);
      web(t, c, tm);
      g.restore();
    });
    if (t > tm.sign) Civic.tool('quill', PX + PW / 2 - 60, PY + PH / 2 - 120, 150, { rot: 0.55 + 0.1 * Math.sin(Math.min(t, tm.signed + 0.6) * 14) * P(t, tm.signed, tm.signed + 0.1), seed: 791, s: pop(t, tm.sign, 0.35) });
    stampMark('SIGNED', PX + 150, PY + PH / 2 - 92, stampS(t, tm.signed + 0.6) * 0.9, { rot: -0.12, size: 84 });
    // Rex keeps the gavel (s5_08): it bangs on "gavel"
    const st = stage(t, part, part.kind === 'lines' ? [[0, 'inset'], [tm.shr, 'two']] : [[0, 'two']]);
    cast(t, ctx, st, { front: () => {
      if (t < tm.shr + 0.8) return;
      const hit = E.inCubic(P(t, tm.gavel - 0.3, tm.gavel)), bounce = Math.sin(Math.PI * P(t, tm.gavel, tm.gavel + 0.3)) * 0.25;
      Civic.tool('gavel', st.rex.x + 170, st.rex.y - st.rex.h * 0.42, 150, { rot: lerp(-0.9, 0.2, hit) - bounce, seed: 792, s: pop(t, tm.shr + 0.8, 0.35) });
    } });
  };
  S.s5 = {
    timing: { lead: { lines: CARD.lead }, hold: { lines: 3.0 }, gaps: { s5_06: 1.0, s5_08: 0.8 } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      CHK.forEach(k => { if (!k.old) out.push({ name: 'scribble', t: c.W(k.cue[0], k.cue[1]) + (k.d || 0), gain: 0.2 }); });
      out.push({ name: 'paper', t: c.W('s5_03', 'trial'), gain: 0.3 }, { name: 'paper', t: c.W('s5_05', 'rule'), gain: 0.4 }, { name: 'whoosh', t: c.L('s5_06') - 0.3, gain: 0.2 });
      out.push({ name: fx('unroll', 'paper'), t: c.L('s5_06') + 0.1, gain: 0.45 });
      ['council', 'carry', 'court'].forEach(w => out.push({ name: 'scribble', t: c.W('s5_06', w), gain: 0.2 }));
      const sg = c.W('s5_08', 'Signed');
      out.push({ name: 'pop', t: c.W('s5_07', 'Sign'), gain: 0.3 }, { name: 'scribble', t: sg, gain: 0.3 }, { name: 'stamp', t: sg + 0.6, gain: 0.6 });
      out.push({ name: fx('gavel', 'thunk'), t: c.W('s5_08', 'gavel') - 0.02, gain: 0.6 }, { name: fx('cheer', 'sparkle'), t: c.E('s5_08') + 0.2, gain: 0.45 });
      return out;
    },
  };

  /* wrap every scene: the chapter card that tears away to the scene; sfx outside the part are dropped */
  for (const id of Object.keys(BODY)) {
    S[id].draw = function (t, part, ctx) {
      const c = cues(ctx), i = segIndex(part.seg);
      if (part.kind === 'lines' && t < CARD.hold + CARD.tear) {
        if (t < CARD.hold) drawCard(i);
        else tornTransition(() => drawCard(i), u => BODY[id](u, part, ctx, c), t, CARD.hold, CARD.tear, 40 + i);
      } else BODY[id](t, part, ctx, c);
    };
    const sfx = S[id].sfx;
    S[id].sfx = (part, ctx) => sfx(part, ctx).filter(e => e && e.name && e.t > -1e5 && e.t < 1e5);
  }

  const BranchesVideo = window.BranchesVideo = { BODY, nums, working, STEPCUE, CHK, layers: LY, lastSteps: null };

  class VideoScene extends Player.Scene {
    enter() {
      NUMS = null; WK.clear();          // recomputed from the loaded content
      super.enter();
      // bake the backdrops in spare frame time while the first chapter card is up
      if (typeof prewarm === 'function') { prewarm.draw(() => bgGym()); prewarm.draw(() => bgBoard()); prewarm.draw(() => bgCourt()); prewarm.draw(() => bgParch()); }
    }
  }
  Levels.video = VideoScene;
})();
